import { httpsCallable } from "firebase/functions";
import type { CSSProperties } from "react";
import { useEffect, useState } from "react";
import { functions } from "../firebase";

type AnalyticsSummary = {
  configured: boolean;
  message?: string;
  propertyId?: string;
  dateRangeLabel?: string;
  overview?: {
    activeUsers: number;
    realtimeActiveUsers?: number;
    sessions: number;
    pageViews: number;
    engagedSessions: number;
    engagementRate: number;
    activeUsersChangePct: number | null;
    sessionsChangePct: number | null;
    pageViewsChangePct: number | null;
  };
  topPages?: Array<{ path: string; title: string; pageViews: number; activeUsers: number }>;
  trafficSources?: Array<{ channel: string; source: string; sessions: number; activeUsers: number }>;
  devices?: Array<{ category: string; sessions: number }>;
  ctas?: Array<{ eventName: string; label: string; eventCount: number }>;
};

function fmt(value: number | undefined) {
  return Number(value ?? 0).toLocaleString();
}

function trend(value: number | null | undefined) {
  if (value === null || value === undefined) return "No comparison";
  if (value > 0) return `+${value}%`;
  return `${value}%`;
}

function pct(value: number, total: number) {
  if (!total || total <= 0) return 0;
  return Math.max(0, Math.min(100, Math.round((value / total) * 100)));
}

function cleanTitle(title: string) {
  return title.replace(/\s*\|\s*Zion Classical Academy\s*$/i, "").trim();
}

function Kpi({ label, value, change, helper }: { label: string; value: number | string; change?: number | null; helper?: string }) {
  const positive = typeof change === "number" && change > 0;
  const negative = typeof change === "number" && change < 0;
  return (
    <div className="analytics-kpi">
      <div className="analytics-kpi-label">{label}</div>
      <div className="analytics-kpi-value">{typeof value === "number" ? fmt(value) : value}</div>
      <div className={`analytics-trend ${positive ? "positive" : ""} ${negative ? "negative" : ""}`}>
        {helper || `${trend(change)} vs prior period`}
      </div>
    </div>
  );
}

function AnalyticsCard({ title, subtitle, empty, children }: { title: string; subtitle?: string; empty: boolean; children: React.ReactNode }) {
  return (
    <div className="card analytics-card">
      <div className="analytics-card-heading">
        <h3>{title}</h3>
        {subtitle ? <p className="muted">{subtitle}</p> : null}
      </div>
      {empty ? <p className="muted">No data yet.</p> : children}
    </div>
  );
}

function BarList({
  rows,
  max,
  getKey,
  getLabel,
  getMeta,
  getValue,
  tone = "blue",
}: {
  rows: any[];
  max: number;
  getKey: (row: any) => string;
  getLabel: (row: any) => string;
  getMeta?: (row: any) => string;
  getValue: (row: any) => number;
  tone?: "blue" | "gold" | "green";
}) {
  return (
    <div className="analytics-bar-list">
      {rows.map((row) => {
        const value = getValue(row);
        return (
          <div className="analytics-bar-row" key={getKey(row)}>
            <div className="analytics-bar-row-top">
              <div>
                <b>{getLabel(row)}</b>
                {getMeta ? <span>{getMeta(row)}</span> : null}
              </div>
              <strong>{fmt(value)}</strong>
            </div>
            <div className="analytics-bar-track">
              <div className={`analytics-bar-fill ${tone}`} style={{ width: `${pct(value, max)}%` }} />
            </div>
          </div>
        );
      })}
    </div>
  );
}

function DeviceMix({ rows }: { rows: Array<{ category: string; sessions: number }> }) {
  const total = rows.reduce((sum, row) => sum + row.sessions, 0);
  const top = rows[0];
  const topPct = top ? pct(top.sessions, total) : 0;
  return (
    <div className="analytics-device-layout">
      <div className="dashboard-donut dashboard-donut-blue" style={{ "--pct": `${topPct}%` } as CSSProperties}>
        <div>
          <b>{topPct}%</b>
          <span>{top?.category || "device"}</span>
        </div>
      </div>
      <div className="analytics-mini-list">
        {rows.map((row) => (
          <div key={row.category}>
            <span style={{ textTransform: "capitalize" }}>{row.category}</span>
            <b>{fmt(row.sessions)}</b>
          </div>
        ))}
      </div>
    </div>
  );
}

export function PublicWebAnalyticsPage() {
  const [data, setData] = useState<AnalyticsSummary | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  async function load() {
    setLoading(true);
    setError("");
    try {
      const fn = httpsCallable(functions, "getPublicWebAnalyticsSummary");
      const res: any = await fn({});
      setData(res?.data ?? null);
    } catch (e: any) {
      setError(e?.message || "Failed to load public web analytics.");
      setData(null);
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    void load();
  }, []);

  const overview = data?.overview;
  const topPages = data?.topPages || [];
  const trafficSources = data?.trafficSources || [];
  const ctas = data?.ctas || [];
  const devices = data?.devices || [];
  const maxPageViews = Math.max(...topPages.map((row) => row.pageViews), 1);
  const maxSourceSessions = Math.max(...trafficSources.map((row) => row.sessions), 1);
  const maxCtaClicks = Math.max(...ctas.map((row) => row.eventCount), 1);

  return (
    <div className="card dashboard-shell analytics-dashboard">
      <div className="analytics-header">
        <div>
          <h2>Public Web Analytics</h2>
          <p className="muted">
            A quick read on who is visiting, what they are viewing, and which calls to action are getting attention.
          </p>
          {data?.propertyId ? <p className="analytics-property">GA4 property <code>{data.propertyId}</code></p> : null}
        </div>
        <button className="btn secondary" onClick={() => void load()} disabled={loading}>
          {loading ? "Loading..." : "Refresh"}
        </button>
      </div>

      <hr className="hr" />

      {loading ? <p className="muted">Loading analytics...</p> : null}
      {error ? <p className="error">{error}</p> : null}

      {!loading && data && !data.configured ? (
        <div className="card dashboard-panel">
          <h3>Analytics Not Configured</h3>
          <p className="muted">{data.message}</p>
          <ol>
            <li>Create or confirm the GA4 web data stream for the public website.</li>
            <li>Add <code>VITE_GA_MEASUREMENT_ID</code> to <code>public-web</code> env files and redeploy public-web.</li>
            <li>Set <code>GA4_PROPERTY_ID</code> for Firebase Functions.</li>
            <li>Grant the Firebase Functions runtime service account Viewer access to that GA4 property.</li>
          </ol>
        </div>
      ) : null}

      {overview ? (
        <>
          <div className="analytics-hero">
            <div>
              <span className="analytics-eyebrow">Realtime</span>
              <h3>Visitors Right Now</h3>
              <p className="muted">This updates from GA4 realtime and is the fastest way to confirm tracking is alive.</p>
            </div>
            <div className="analytics-live-number">
              <b>{fmt(overview.realtimeActiveUsers)}</b>
              <span>active now</span>
            </div>
          </div>

          <div className="analytics-section-title">
            <div>
              <span className="analytics-eyebrow">Trend</span>
              <h3>{data?.dateRangeLabel || "Last 30 days"}</h3>
            </div>
            <p className="muted">Compared with the prior 30-day period.</p>
          </div>
          <div className="analytics-kpi-grid">
            <Kpi label="30-Day Active Users" value={overview.activeUsers} change={overview.activeUsersChangePct} />
            <Kpi label="Sessions" value={overview.sessions} change={overview.sessionsChangePct} />
            <Kpi label="Page Views" value={overview.pageViews} change={overview.pageViewsChangePct} />
            <Kpi label="Engagement Rate" value={`${overview.engagementRate}%`} helper={`${fmt(overview.engagedSessions)} engaged sessions`} />
          </div>

          <hr className="hr" />

          <div className="analytics-detail-grid">
            <AnalyticsCard title="Top Pages" subtitle="Where families are spending time." empty={!topPages.length}>
              <BarList
                rows={topPages}
                max={maxPageViews}
                getKey={(row) => row.path}
                getLabel={(row) => row.path}
                getMeta={(row) => `${cleanTitle(row.title)} - ${fmt(row.activeUsers)} visitors`}
                getValue={(row) => row.pageViews}
              />
            </AnalyticsCard>

            <AnalyticsCard title="Traffic Sources" subtitle="How people are finding the site." empty={!trafficSources.length}>
              <BarList
                rows={trafficSources}
                max={maxSourceSessions}
                getKey={(row) => `${row.channel}-${row.source}`}
                getLabel={(row) => row.channel}
                getMeta={(row) => row.source}
                getValue={(row) => row.sessions}
                tone="green"
              />
            </AnalyticsCard>

            <AnalyticsCard title="CTA Clicks" subtitle="Important buttons and next steps." empty={!ctas.length}>
              <BarList
                rows={ctas}
                max={maxCtaClicks}
                getKey={(row) => row.eventName}
                getLabel={(row) => row.label}
                getValue={(row) => row.eventCount}
                tone="gold"
              />
            </AnalyticsCard>

            <AnalyticsCard title="Device Mix" subtitle="Desktop, mobile, and tablet sessions." empty={!devices.length}>
              <DeviceMix rows={devices} />
            </AnalyticsCard>
          </div>
        </>
      ) : null}
    </div>
  );
}
